import { __awaiter } from "tslib";
import ApiManager from '@/managers/apimanagers';
import { ref } from 'vue';
import { useMessage } from '@/const/messages';
import { useStore } from '@/store/store';
import { decimalStep, priceDecimalStep, amountDecimalStep } from './utils';
import { useSound } from './sound';
import BigNumber from 'bignumber.js';
export const ORDER_COEFFICIENT = 1.1;
/**
 * @summary 注文機能
 * @returns
 */
export const useOrder = () => {
    const isLoading = ref(false);
    const isPostFaild = ref(false);
    /**
     * @summary 誤発注防止用に一定以上のマーケット価格からの乖離があった場合にConfirmで注意喚起するか判断する
     * @param price 入力価格
     * @param markPrice マーケット価格
     * @param isMarketOrder Market Orderならtrue
     * @return 適正価格範囲内ならtrue
     */
    const isFairPrice = (price, markPrice, isMarketOrder) => {
        if (isMarketOrder) {
            return true;
        }
        const cofficient = process.env.VUE_APP_ORDER_CONFIRM_PER / 100;
        const under = Number(markPrice) * (1 - cofficient);
        const upper = Number(markPrice) * (1 + cofficient);
        return under < price && price < upper;
    };
    /**
     * @summary order時のバリデーション
     * @param price 価格
     * @param amount 数量
     * @param isLimit 注文方式がLimitならtrue Marketならfalse
     * @param isSell sellならfalse buyならtrue
     * @param balances ユーザーのバランス情報
     * @param base base通貨
     * @param quote quote通貨
     * @param isAmountMode MarketOrder時に数量指定ならtrue、金額指定ならfalse
     * @param latestValue 最新の歩み値（数量指定の場合のみ使用）
     */
    const validate = (price, amount, isLimit, isSell, balances, base, quote, isAmountMode, latestValue = 0, pair, currencyPairInfo) => {
        const { ORDER_MESSAGES } = useMessage();
        const currentPairInfo = currencyPairInfo.find((c) => c.pair === pair);
        // 最小注文量に満たない場合はエラーとする
        if ((isLimit || (!isLimit && isAmountMode)) && currentPairInfo) {
            if (amount < currentPairInfo.minSize) {
                return ORDER_MESSAGES.BALANCE_LESS_THAN_AMOUNT;
            }
        }
        const adjust = Math.pow(10, 8);
        const minus = (balance, onHold) => {
            return (Number(balance) * adjust - Number(onHold) * adjust) / adjust;
        };
        const baseBalance = balances.find((b) => b.symbol === base);
        const quoteBalance = balances.find((b) => b.symbol === quote);
        // 資産の口座情報が見つからない場合
        if (!quoteBalance || !baseBalance) {
            return ORDER_MESSAGES.WALLET_NOT_FOUND;
        }
        // 売りの時
        if (isSell) {
            // 利用可能残高を超える注文は不可
            const availableBalance = minus(baseBalance.balance, baseBalance.onHold);
            if (amount > availableBalance) {
                return ORDER_MESSAGES.DONT_OWN_BALANCE;
            }
        }
        // 買いの時
        if (!isSell) {
            if (!isLimit) {
                const availableBalance = minus(quoteBalance.balance, quoteBalance.onHold);
                if (isAmountMode) {
                    // Market Orderで数量指定の買いの場合に最新の約定値に係数をかけた金額以上ない場合
                    const border = amount * latestValue * ORDER_COEFFICIENT;
                    if (border > availableBalance) {
                        return ORDER_MESSAGES.REQUIRE_ENOUGH_BALANCE;
                    }
                }
                else {
                    // Market Orderで金額指定の買いの場合に指定金額の資産を保有していない場合
                    if (price > availableBalance) {
                        return ORDER_MESSAGES.REQUIRE_ENOUGH_BALANCE;
                    }
                }
            }
        }
        // Limit Orderで価格・数量のどちらかが0以下の場合
        if (isLimit && (price <= 0 || amount <= 0)) {
            return ORDER_MESSAGES.PRICE_OR_AMOUNT_0;
        }
        if (!isLimit) {
            // Market Orderで数量指定の時数量が0以下の場合
            const isPrice0 = !isAmountMode && price <= 0;
            const isAmount0 = isAmountMode && amount <= 0;
            if (isPrice0 || isAmount0) {
                return ORDER_MESSAGES.PRICE_OR_AMOUNT_0;
            }
        }
        return null;
    };
    /**
     * @summary 注文処理
     * @param price 価格（LimitOrderの場合のみ使用）
     * @param amount 数量
     * @param trade BuyまたはSell
     * @param order LimitまたはMarket
     * @param isAmountMode MarketOrder時に数量指定ならtrue、金額指定ならfalse
     */
    const postOrder = (pair, price, amount, trade, order, isAmountMode) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            isLoading.value = true;
            const tradeType = trade === 'Buy' ? 'buy' : 'sell';
            const orderType = order === 'Limit' ? 'limit' : 'market';
            const request = {
                pair,
                price,
                amount,
                tradeType,
                orderType,
            };
            if (order === 'Market') {
                if (isAmountMode) {
                    request.price = 0;
                }
                else {
                    request.amount = 0;
                }
            }
            return yield ApiManager.post('/order', request);
        }
        catch (err) {
            isPostFaild.value = true;
            throw err;
        }
        finally {
            isLoading.value = false;
        }
    });
    /**
     * @summary 注文キャンセル処理
     * @param id 注文ID
     * @param pair 通貨ペア
     */
    const postCancel = (id, pair) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            isLoading.value = true;
            const request = { id, pair };
            return yield ApiManager.post('/cancel', request);
        }
        catch (err) {
            isPostFaild.value = true;
            throw err;
        }
        finally {
            isLoading.value = false;
        }
    });
    return { isPostFaild, isLoading, postOrder, postCancel, validate, isFairPrice };
};
/**
 * @summary Orderの状態保持と切り替え関数系
 * @param quote
 * @param base
 * @param pair
 * @returns
 */
export const useOrderDisplay = (quote, base, pair, currencyInfo, currencyPairInfo) => {
    const store = useStore();
    const { click, play } = useSound();
    const { format, ORDER } = useMessage();
    /** Status */
    const tradeType = ref('Limit');
    const orderType = ref('Buy');
    const isShowConfirm = ref(false);
    const marketMode = ref('Amount');
    const percentage = ref(0);
    /** Consts */
    const quoteStep = decimalStep(quote, currencyInfo);
    const limitPriceStep = priceDecimalStep(pair, currencyPairInfo);
    const baseStep = amountDecimalStep(base, currencyInfo);
    /**
     * @summary Limit Market区分変更
     * @param changedTrade 切り替え先
     */
    const onClickTradeType = (changedTrade) => {
        tradeType.value = changedTrade;
        if (changedTrade === 'Market') {
            const decimal = String(Math.pow(quoteStep, -1)).length - 1;
            store.state.order.price = Number(store.state.order.price).toFixed(decimal);
        }
        onChangeRange(percentage.value);
        play(click);
    };
    /**
     * @summary Market Orderの買いのときにAmoutかPriceに切り替える場合の処理
     * @param changedMarketMode 切り替え先
     */
    const marketModeChange = (changedMarketMode) => {
        marketMode.value = changedMarketMode;
        onChangeRange(percentage.value);
    };
    /**
     * @summary Buy Sell区分変更
     * @param changedOrder 切り替え先
     */
    const onChangeDeal = (changedOrder) => {
        orderType.value = changedOrder;
        if (changedOrder === 'Sell') {
            marketMode.value = 'Amount';
        }
        onChangeRange(percentage.value);
        play(click);
    };
    /**
     * @summary rangeの%変更時のPriceまたはAmoutの値セット
     * @param per rangeの%
     */
    const onChangeRange = (per) => {
        percentage.value = per;
        // 金額指定の成行
        if (tradeType.value === 'Market' && marketMode.value === 'Price') {
            store.state.order.price = _changeRange(store.state.balances, per, true, quoteStep);
            return;
        }
        // 数量指定の成行買い
        if (tradeType.value === 'Market' && marketMode.value === 'Amount' && orderType.value === 'Buy') {
            store.state.order.amount = _changeBuyRange(store.state.balances, per, store.state.latestValue, baseStep);
            return;
        }
        // 指値買い
        if (tradeType.value === 'Limit' && orderType.value === 'Buy') {
            store.state.order.amount = _changeBuyRange(store.state.balances, per, Number(store.state.order.price), baseStep);
            return;
        }
        // デフォルト
        store.state.order.amount = _changeRange(store.state.balances, per, false, baseStep);
    };
    /**
     * @summary rangeの%変更時の処理 買いの場合
     * @param balances ユーザーのバランス情報
     * @param percentage rangeの%
     * @param basePrice 基準価格
     * @param step 桁数0.0001
     */
    const _changeBuyRange = (balances, percentage, basePrice, step) => {
        const quoteSymbol = balances.find((b) => b.symbol === quote);
        if (!quoteSymbol || basePrice === 0) {
            return 0;
        }
        const balance = quoteSymbol.balance;
        const onHold = quoteSymbol.onHold;
        const div = _btdiv(balance - onHold, basePrice, step);
        return _btmul(div, percentage / 100, step);
    };
    /**
     * @summary 買いの場合のrangeの%変更時の処理
     * @param balances ユーザーのバランス情報
     * @param percentage rangeの%
     * @param step 桁数0.0001
     */
    const _changeRange = (balances, percentage, isPriceMode = false, step) => {
        const symbol = balances.find((b) => {
            return isPriceMode ? b.symbol === quote : b.symbol === base;
        });
        const balanceByUser = symbol === null || symbol === void 0 ? void 0 : symbol.balance;
        const onHoldByUser = symbol === null || symbol === void 0 ? void 0 : symbol.onHold;
        if (balanceByUser && onHoldByUser !== null && onHoldByUser !== undefined) {
            const minus = _bcMinus(balanceByUser, onHoldByUser, step);
            return _btmul(minus, percentage / 100, step);
        }
        return 0;
    };
    const _bcMinus = (target1, target2, step) => {
        const mul = (new BigNumber(1)).div(new BigNumber(step)).toNumber();
        const resizeTarget1 = target1 * mul;
        const resizeTarget2 = target2 * mul;
        return (resizeTarget1 - resizeTarget2) / mul;
    };
    const _btmul = (target1, target2, step) => {
        const mul = (new BigNumber(1)).div(new BigNumber(step)).toNumber();
        return Math.floor(target1 * target2 * mul) / mul;
    };
    const _btdiv = (target1, target2, step) => {
        const mul = (new BigNumber(1)).div(new BigNumber(step)).toNumber();
        return Math.floor((target1 / target2) * mul) / mul;
    };
    /**
     * @summary Confirm画面の表示処理
     * @param isAuth ログイン済みかどうか
     * @param onOrder Order処理
     */
    const showConfirm = (isAuth, onOrder) => __awaiter(void 0, void 0, void 0, function* () {
        if (!isAuth) {
            location.href = '/members/sign_in';
            return;
        }
        if (store.state.userSettings.isOrderLeave) {
            yield onOrder(false);
            return;
        }
        isShowConfirm.value = true;
    });
    /**
     * @summary Confirm画面のClose処理
     */
    const modalClose = () => {
        isShowConfirm.value = false;
    };
    /**
     * @summary ボタンの色設定処理
     */
    const setColorMode = (isAuth) => {
        if (!isAuth) {
            return 'login';
        }
        return orderType.value === 'Buy' ? 'long' : 'short';
    };
    /**
     * @summary ボタンのテキスト設定処理
     */
    const setButtonText = (isAuth) => {
        const side = format(orderType.value === 'Buy' ? ORDER.BUY : ORDER.SELL);
        return !isAuth ? format(ORDER.LOGIN) : `${side} ${store.getters.symbol.base}`;
    };
    return {
        onClickTradeType,
        marketModeChange,
        onChangeDeal,
        onChangeRange,
        showConfirm,
        modalClose,
        setColorMode,
        setButtonText,
        quoteStep,
        baseStep,
        limitPriceStep,
        tradeType,
        orderType,
        isShowConfirm,
        marketMode,
        percentage,
    };
};
